import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash, PencilSimple, ShieldCheck } from "@phosphor-icons/react";

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [meta, setMeta] = useState({ screens: [], actions: [] });
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [r, m] = await Promise.all([api.get("/roles"), api.get("/roles/meta")]);
    setRoles(r.data); setMeta(m.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ name: "", display_name: "", permissions: {} });
    setModal({ item: null }); setErr("");
  };
  const openEdit = (r) => {
    setForm({ ...r, permissions: r.permissions || {} });
    setModal({ item: r }); setErr("");
  };

  const togglePerm = (screen, action) => {
    setForm((f) => {
      const perms = { ...(f.permissions || {}) };
      const set = new Set(perms[screen] || []);
      if (set.has(action)) set.delete(action); else set.add(action);
      if (set.size === 0) delete perms[screen]; else perms[screen] = Array.from(set);
      return { ...f, permissions: perms };
    });
  };

  const save = async () => {
    try {
      if (modal.item) await api.patch(`/roles/${modal.item.id}`, form);
      else await api.post("/roles", form);
      setModal(null); load();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this role?")) return;
    try { await api.delete(`/roles/${id}`); load(); } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="roles-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-small text-blue-700">Access Control</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>Custom Roles</h1>
          <p className="text-sm text-slate-600 mt-1">Define screen-and-transaction level permissions. Users mapped to a custom role see only the screens granted here.</p>
        </div>
        <button data-testid="new-role-button" onClick={openCreate} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md inline-flex items-center gap-2 transition-colors">
          <Plus size={14} weight="bold" /> New role
        </button>
      </header>

      <div className="flat-card">
        <table className="data-table">
          <thead><tr><th>Name</th><th>Display Name</th><th>Screens granted</th><th>Type</th><th></th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="text-center py-6 text-slate-500">Loading…</td></tr> :
              roles.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-slate-500">No custom roles yet. Built-in roles always exist.</td></tr> :
                roles.map((r) => (
                  <tr key={r.id} data-testid={`role-row-${r.name}`}>
                    <td className="font-mono font-semibold text-slate-900">{r.name}</td>
                    <td>{r.display_name}</td>
                    <td className="text-xs text-slate-600">{Object.keys(r.permissions || {}).length} screens</td>
                    <td>{r.system ? <span className="label-small text-slate-500">System</span> : <span className="label-small text-blue-700">Custom</span>}</td>
                    <td className="text-right">
                      {!r.system && <>
                        <button onClick={() => openEdit(r)} className="p-1.5 rounded text-slate-500 hover:text-blue-600 transition-colors"><PencilSimple size={15} /></button>
                        <button onClick={() => remove(r.id)} className="p-1.5 rounded text-slate-500 hover:text-red-600 transition-colors"><Trash size={15} /></button>
                      </>}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-slate-200 rounded-md max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="role-modal">
            <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Manrope" }}>{modal.item ? "Edit" : "New"} Role</h3>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="label-small block mb-1">Name (system)</label>
                <input data-testid="role-name" disabled={!!modal.item} value={form.name || ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 disabled:bg-slate-50 font-mono" />
              </div>
              <div>
                <label className="label-small block mb-1">Display name</label>
                <input data-testid="role-display-name" value={form.display_name || ""} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600" />
              </div>
            </div>

            <div className="label-small mb-2">Permissions matrix</div>
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Screen</th>
                    {meta.actions.map((a) => <th key={a} className="text-center capitalize">{a}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {meta.screens.map((screen) => (
                    <tr key={screen}>
                      <td className="capitalize font-semibold text-slate-800">{screen.replace("_", " ")}</td>
                      {meta.actions.map((action) => (
                        <td key={action} className="text-center">
                          <input
                            type="checkbox"
                            data-testid={`perm-${screen}-${action}`}
                            checked={(form.permissions?.[screen] || []).includes(action)}
                            onChange={() => togglePerm(screen, action)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {err && <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

            <div className="flex justify-end gap-2 pt-5 mt-4 border-t border-slate-100">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-md transition-colors">Cancel</button>
              <button data-testid="role-save" onClick={save} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md transition-colors">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
